/**
 * Helpers purs du widget météo : mapping code WMO → icône/couleur, formatage
 * date FR, agrégation 7 jours, recherche de l'heure "maintenant".
 *
 * Pas de React ici — fonctions purs testables en isolation.
 */

import {
  Cloud,
  CloudDrizzle,
  CloudFog,
  CloudLightning,
  CloudRain,
  CloudSnow,
  Sun,
} from "lucide-react";

import { JOURS_COURT, JOURS_LONG, MOIS_LONG } from "./constants.js";
import type { JourMeteo, MeteoPoint, VariableConfig, WeatherMeta } from "./types.js";

/**
 * Mappe un code météo WMO vers icône + libellé + COULEUR d'icône.
 * La couleur permet à l'œil de scanner une journée d'un coup (soleil jaune
 * vs nuages gris vs pluie bleue), au lieu d'un défilé d'icônes champagne
 * indifférenciées.
 */
export function getWeatherMeta(code: number): WeatherMeta {
  if (code === 0) return { icon: Sun, label: "Ciel clair", color: "#E8B547" }; // jaune doré
  if (code === 1) return { icon: Sun, label: "Peu nuageux", color: "#D9B86A" }; // jaune-beige
  if (code === 2) return { icon: Cloud, label: "Partiellement nuageux", color: "#8a99a8" }; // gris-bleu
  if (code === 3) return { icon: Cloud, label: "Couvert", color: "#7a8896" };
  if (code === 45 || code === 48) return { icon: CloudFog, label: "Brouillard", color: "#9CA3AF" };
  if (code >= 51 && code <= 57) return { icon: CloudDrizzle, label: "Bruine", color: "#7B96B0" };
  if (code >= 61 && code <= 67) return { icon: CloudRain, label: "Pluie", color: "#5B7A95" };
  if (code >= 71 && code <= 77) return { icon: CloudSnow, label: "Neige", color: "#B6CCDB" };
  if (code >= 80 && code <= 82) return { icon: CloudRain, label: "Averses", color: "#5B7A95" };
  if (code >= 85 && code <= 86)
    return { icon: CloudSnow, label: "Averses neige", color: "#B6CCDB" };
  if (code === 95) return { icon: CloudLightning, label: "Orage", color: "#7C5BC0" };
  if (code === 96 || code === 99)
    return { icon: CloudLightning, label: "Orage + grêle", color: "#6B3FB0" };
  return { icon: Cloud, label: "—", color: "#94A3B8" };
}

/**
 * Score d'intensité d'un code WMO — utilisé pour départager des codes ex-aequo
 * dans `codeMeteoDominantJour`. Plus le score est haut, plus la condition
 * est marquante (priorité aux conditions visibles : pluie/neige/orage).
 */
export function scoreIntensite(code: number): number {
  if (code === 95 || code === 96 || code === 99) return 6;
  if (code >= 80) return 5;
  if (code >= 71) return 5;
  if (code >= 61) return 4;
  if (code >= 51) return 3;
  if (code === 45 || code === 48) return 2;
  if (code >= 1 && code <= 3) return 1;
  return 0;
}

/**
 * Code météo dominant sur les heures de jour (6h-20h) d'une journée.
 * Compte les occurrences, retourne le code le plus fréquent. En cas
 * d'égalité, priorité au code de plus haute intensité (pour ne pas
 * masquer une demi-journée de pluie derrière une demi-journée de soleil).
 */
export function codeMeteoDominantJour(points: MeteoPoint[]): number {
  const counts = new Map<number, number>();
  for (const p of points) {
    const heure = Number(p.time.slice(11, 13));
    if (heure < 6 || heure > 20) continue; // ignore la nuit (peu pertinent visuellement)
    counts.set(p.weather_code, (counts.get(p.weather_code) ?? 0) + 1);
  }
  if (counts.size === 0) return points[0]?.weather_code ?? 0;
  let bestCode = 0;
  let bestCount = 0;
  for (const [code, count] of counts) {
    if (
      count > bestCount ||
      (count === bestCount && scoreIntensite(code) > scoreIntensite(bestCode))
    ) {
      bestCount = count;
      bestCode = code;
    }
  }
  return bestCode;
}

/**
 * Trouve l'index dans `hourly` correspondant à l'heure actuelle locale.
 * Match exact sur le tag `YYYY-MM-DDTHH:00`, sinon fallback sur l'écart
 * temporel minimum (utile si la grille horaire est décalée).
 */
export function trouverIndexMaintenant(hourly: MeteoPoint[]): number {
  if (hourly.length === 0) return 0;
  const maintenant = new Date();
  const tag = `${maintenant.getFullYear()}-${String(maintenant.getMonth() + 1).padStart(2, "0")}-${String(maintenant.getDate()).padStart(2, "0")}T${String(maintenant.getHours()).padStart(2, "0")}:00`;
  const idx = hourly.findIndex((p) => p.time === tag);
  if (idx >= 0) return idx;
  const nowMs = maintenant.getTime();
  let bestIdx = 0;
  let bestDelta = Infinity;
  for (let i = 0; i < hourly.length; i++) {
    const p = hourly[i];
    if (!p) continue;
    const delta = Math.abs(Date.parse(p.time) - nowMs);
    if (delta < bestDelta) {
      bestDelta = delta;
      bestIdx = i;
    }
  }
  return bestIdx;
}

/**
 * Agrège les 168h en 7 jours avec min/max + icône représentative.
 *
 * L'icône retenue est le code météo DOMINANT du jour (le plus fréquent sur
 * les heures de jour 6h-20h). Un jour avec 22h de soleil et 2h de nuages
 * reste "soleil". Cf. `codeMeteoDominantJour` pour le tri ex-aequo.
 */
export function agregerParJour(hourly: MeteoPoint[], indexMaintenant: number): JourMeteo[] {
  const buckets = new Map<string, { points: MeteoPoint[]; date: Date }>();
  for (const p of hourly) {
    const dateStr = p.time.slice(0, 10);
    let bucket = buckets.get(dateStr);
    if (!bucket) {
      bucket = { points: [], date: new Date(p.time) };
      buckets.set(dateStr, bucket);
    }
    bucket.points.push(p);
  }
  const dateAujourdhui = hourly[indexMaintenant]?.time.slice(0, 10) ?? null;
  return Array.from(buckets.values()).map((b) => {
    let min = Infinity;
    let max = -Infinity;
    for (const p of b.points) {
      if (p.temperature_c < min) min = p.temperature_c;
      if (p.temperature_c > max) max = p.temperature_c;
    }
    const iconCode = codeMeteoDominantJour(b.points);
    const dateStr = b.points[0]?.time.slice(0, 10) ?? "";
    return {
      estAujourdhui: dateStr === dateAujourdhui,
      libelle: dateStr === dateAujourdhui ? "AUJ." : (JOURS_COURT[b.date.getDay()] ?? ""),
      min: Math.round(min),
      max: Math.round(max),
      iconCode,
    };
  });
}

/** Format long FR pour le panneau "heure explorée" (ex: "Mer. 14 mai à 09h00"). */
export function formatHeureLongue(timeIso: string): string {
  const d = new Date(timeIso);
  return `${JOURS_LONG[d.getDay()]} ${d.getDate()} ${MOIS_LONG[d.getMonth()]} à ${String(d.getHours()).padStart(2, "0")}h00`;
}

/**
 * Calcule les bornes Y du graphique pour une variable donnée.
 * Si `config.bornes` est défini → bornes fixes. Sinon, auto-scale sur la
 * fenêtre 7j avec un padding 10% (min 2 unités) arrondi sur 5.
 */
export function calculerBornesY(
  hourly: MeteoPoint[],
  config: VariableConfig,
): { yMin: number; yMax: number } {
  if (config.bornes) return { yMin: config.bornes.min, yMax: config.bornes.max };
  let min = Infinity;
  let max = -Infinity;
  for (const p of hourly) {
    const v = config.extracteur(p);
    if (v < min) min = v;
    if (v > max) max = v;
  }
  if (!isFinite(min) || !isFinite(max)) return { yMin: 0, yMax: 1 };
  const padding = Math.max(2, Math.round((max - min) * 0.1));
  return {
    yMin: Math.floor((min - padding) / 5) * 5,
    yMax: Math.ceil((max + padding) / 5) * 5,
  };
}

/** Format relatif compact pour "MAJ il y a X" affiché en pied de widget. */
export function formatFetchedAt(iso: string): string {
  const delta = Date.now() - Date.parse(iso);
  const min = Math.round(delta / 60000);
  if (min < 1) return "à l'instant";
  if (min < 60) return `il y a ${min} min`;
  const h = Math.round(min / 60);
  return `il y a ${h} h`;
}
